// Console Team: who can sign in to the console, how much of it they reach, and
// which clients. The October hand-off's console-team.jsx, on the live records
// of 149 and 150, to Gabe's rulings of 11 October where they and the prototype
// differ:
//
//   - Admin is Nova staff: every client, plus Team and Settings. Only an Admin
//     sees this page; the service refuses the list to anybody else.
//   - Editor is Nova staff given clients, several allowed (the chips). Builds
//     and publishes for those, each publish, draw and wheel behind a fresh
//     authenticator code.
//   - Viewer is one company's own people, so the company is one choice, not
//     chips, and their shows are granted one at a time.
//   - Everyone needs an authenticator. BA crew are on the list, marked BA.
//   - Invites do not expire: "Invited" means never signed in, and an Admin can
//     resend or cancel. The prototype's "expires in 7 days" is not drawn.
//   - Nobody can demote or remove the last Admin, or remove themselves. The
//     service refuses both; this page does not offer an Admin their own row's
//     access or Remove, and says the service's sentence for anything else.
//
// THE 12 OCTOBER PAGE SHAPE. DeskPage, with search, the access filter and the
// totals in the right-hand column and Invite someone under the title, as the
// prototype draws it. Every column sorts on the server; the page compares no
// rows. Search and the filter narrow what is drawn out of the rows the server
// sent, as Clients' search does: the list is everybody who can sign in, which
// is tens of people and one read, so there is no pager.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Chip, NoticeBox, PageHead, Switch } from "@nova/desk-ui/parts";
import { Empty, FieldRow, Info, Seg, SecLabel } from "@nova/desk-ui/kit";
import { DeskPage, SortHead, useListSort, type ListSort } from "@nova/desk-ui/page";
import { Confirm, Failed, ListState, Loading } from "@nova/desk-ui/states";
import type { ActivationDirectory, ActivationLine } from "../../../operator/src/activations.js";
import type { AdminRefusal } from "../api/admin.js";
import { readActivations, type ApiOptions } from "../api/client.js";
import {
  changeAccess,
  createTeamMember,
  grantShow,
  readTeam,
  removeTeamMember,
  revokeShow,
  sendInvitation,
  setEditorClients,
  setExport,
  type Access,
  type Invitee,
  type Team as TeamList,
  type TeamClient,
  type TeamPerson,
  type TeamSortKey,
  liveHeld,
} from "../api/team.js";
import type { SessionToken } from "../session/store.js";
import { activationName } from "./activationName.js";
import { AdminRefused } from "./Authorise.js";
import { useConsoleToast } from "./Chrome.js";
import { Dialog } from "./Dialog.js";
import { STATE_TAG, lifecycle } from "./lifecycle.js";

export type TeamProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onSessionEnded: (detail: string) => void;
};

/** The three accesses, in the prototype's order, with the rulings' words. */
const TEAM_ACCESS: readonly (readonly [Access, string, string])[] = [
  ["admin", "Admin", "Nova staff. Every client and every activation, plus Team and Settings."],
  [
    "editor",
    "Editor",
    "Nova staff. Builds and publishes for the clients they are given: registration, kiosk, apps and Ride. No Team, no Settings.",
  ],
  [
    "viewer",
    "Viewer",
    "One client's own people. Dashboards, people and activity for the shows you grant them, read only.",
  ],
];

const ACCESS_NAME: Readonly<Record<Access, string>> = { admin: "Admin", editor: "Editor", viewer: "Viewer" };

/** What each access can open: the prototype's grid, with the rulings' rows. */
const TEAM_GRID: readonly (readonly [string, readonly Access[]])[] = [
  ["Dashboards, people and activity", ["admin", "editor", "viewer"]],
  ["Registration, kiosk, apps and Ride", ["admin", "editor"]],
  ["Publish live, draws and wheels", ["admin", "editor"]],
  ["Add or close an activation", ["admin", "editor"]],
  ["Clients and processing agreements", ["admin"]],
  ["Team and settings", ["admin"]],
];

/** The service's own order before anybody presses a head: Admins, Editors, Viewers, each by surname. */
const TEAM_REST: ListSort<TeamSortKey> = { key: "access", dir: "asc" };

/** Who can export first, and the most recently seen first, when those heads are first pressed. */
const TEAM_FIRST: Readonly<Partial<Record<TeamSortKey, "asc" | "desc">>> = { export: "desc", signin: "desc" };

const FILTERS = ["All", "Admin", "Editor", "Viewer"] as const;
type Filter = (typeof FILTERS)[number];

type Listed =
  | { readonly status: "reading" }
  | { readonly status: "read"; readonly team: TeamList }
  | { readonly status: "failed"; readonly refusal: AdminRefusal };

/** What the Admin has asked to do that needs a second look. */
type Ask =
  | { readonly kind: "access"; readonly person: TeamPerson; readonly to: Access }
  | { readonly kind: "company"; readonly person: TeamPerson; readonly clientId: string }
  | { readonly kind: "remove"; readonly person: TeamPerson }
  | { readonly kind: "shows"; readonly person: TeamPerson };

const sameId = (a: string | null | undefined, b: string | null | undefined): boolean =>
  typeof a === "string" && typeof b === "string" && a.toLowerCase() === b.toLowerCase();

function nameOf(person: TeamPerson): string {
  const name = [person.firstName, person.lastName].filter((part) => part !== null && part.trim() !== "").join(" ");
  return name === "" ? person.email : name;
}

function initialsOf(person: TeamPerson): string {
  const words = nameOf(person)
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2);
  return words.map((word) => word[0]?.toUpperCase() ?? "").join("") || "?";
}

const DAY: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };

function onDay(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleDateString("en-GB", DAY);
}

function atTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return `${at.toLocaleDateString("en-GB", DAY)}, ${at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

export function Team({ api, token, onSessionEnded }: TeamProps): ReactNode {
  const say = useConsoleToast();
  const [listed, setListed] = useState<Listed>({ status: "reading" });
  const [generation, setGeneration] = useState(0);
  const order = useListSort<TeamSortKey>(TEAM_REST, TEAM_FIRST);
  const chosen = order.lit ? order.sort : null;
  const chosenKey = chosen?.key ?? null;
  const chosenDir = chosen?.dir ?? null;
  const [find, setFind] = useState("");
  const [filter, setFilter] = useState<Filter>("All");
  const [inviting, setInviting] = useState(false);
  const [ask, setAsk] = useState<Ask | null>(null);
  /**
   * A row is busy from the moment a write on it is sent until a read of the
   * list asked for after that write has landed: the number is the read it is
   * waiting for, and Infinity while the write itself is in flight. Without the
   * second half the row's controls come back between the write answering and
   * the list being read again, drawn from the list as it was, and a second
   * press acts on that stale state (Bugbot on #206).
   */
  const [busy, setBusy] = useState<ReadonlyMap<string, number>>(() => new Map());
  /** The last read of the list that has landed, by the number reread() gave it. */
  const [landed, setLanded] = useState(0);
  const asked = useRef(0);
  const [problem, setProblem] = useState<{ readonly who: string; readonly refusal: AdminRefusal } | null>(null);
  /** A refused access change, drawn inside the dialog that asked for it rather than behind it. */
  const [askRefusal, setAskRefusal] = useState<AdminRefusal | null>(null);

  useEffect(() => {
    let cancelled = false;
    const reading = generation;
    // The skeleton on the first read only: a re-read for a new order, or after
    // a write, keeps the rows on screen until the answer lands.
    setListed((now) => (now.status === "read" ? now : { status: "reading" }));
    void (async () => {
      const result = await readTeam(
        api,
        token,
        chosenKey === null || chosenDir === null ? null : { key: chosenKey, dir: chosenDir },
      );
      if (cancelled) return;
      setLanded((now) => Math.max(now, reading));
      if (result.ok) {
        setListed({ status: "read", team: result.value });
        return;
      }
      setListed({ status: "failed", refusal: result });
      if (result.kind === "session_ended") onSessionEnded(result.detail);
    })();
    return () => {
      cancelled = true;
    };
  }, [api, token, onSessionEnded, generation, chosenKey, chosenDir]);

  /** Read the list again; answers the number of that read, which `landed` reaches when it has. */
  const reread = (): number => {
    asked.current += 1;
    const read = asked.current;
    setGeneration(read);
    return read;
  };

  const isBusy = (identityId: string): boolean => (busy.get(identityId) ?? -1) > landed;

  /**
   * One write on one person. The row is busy until the write answers and the
   * list has been read again; the answer, where it is the person, replaces the
   * row at once, so nothing is drawn from the list as it was before the write.
   * A refusal is drawn over the list, or handed back with `inline` for a dialog
   * to draw where the person pressing the button is looking.
   */
  async function write(
    person: TeamPerson,
    work: () => Promise<{ ok: true; value: unknown } | ({ ok: false } & AdminRefusal)>,
    done: string,
    { inline = false }: { readonly inline?: boolean } = {},
  ): Promise<{ readonly ok: true } | { readonly ok: false; readonly refusal: AdminRefusal | null }> {
    const id = person.identityId;
    setBusy((now) => new Map(now).set(id, Number.POSITIVE_INFINITY));
    setProblem(null);
    const result = await work();
    if (!result.ok) {
      setBusy((now) => {
        const next = new Map(now);
        next.delete(id);
        return next;
      });
      if (result.kind === "session_ended") {
        onSessionEnded(result.detail);
        return { ok: false, refusal: null };
      }
      if (!inline) setProblem({ who: nameOf(person), refusal: result });
      return { ok: false, refusal: result };
    }
    const answered = result.value as Partial<TeamPerson> | null;
    if (answered !== null && typeof answered === "object" && typeof answered.access === "string" && answered.identityId === id) {
      const fresh = answered as TeamPerson;
      setListed((now) =>
        now.status === "read"
          ? { ...now, team: { ...now.team, people: now.team.people.map((p) => (p.identityId === id ? fresh : p)) } }
          : now,
      );
    }
    say(done);
    const read = reread();
    setBusy((now) => new Map(now).set(id, read));
    return { ok: true };
  }

  const head = (
    <PageHead
      title="Team"
      lede="Who can sign in to the Console, how much of it they reach, and which clients."
      explain="info"
    />
  );

  // A REFUSAL BY POLICY IS THE WHOLE ANSWER: the router draws Team for an
  // Admin only, so a 403 here is the service refusing this sign-in after all,
  // and an Invite form over that sentence would be a control that always
  // refuses. Any other failed read is drawn in the page, below.
  if (listed.status === "failed" && listed.refusal.kind === "not_permitted") {
    return (
      <div className="stack">
        {head}
        <AdminRefused refusal={listed.refusal} />
      </div>
    );
  }

  // THE DESIGN'S LIST STATES (desks.mdc): the head, Find someone and the
  // access table stay while the list is read and when it fails, and only the
  // answer under People is the skeleton, or the kit's Failed with Ask again.
  // Invite someone waits for the read, because the form offers its clients.
  const team = listed.status === "read" ? listed.team : null;
  const people = team?.people ?? [];
  const clients = team?.clients ?? [];
  const answer =
    listed.status === "reading" ? (
      <Loading rows={5} cols={6} label="Loading the team…" />
    ) : listed.status === "failed" ? (
      <Failed
        what="Could not load the team"
        detail={listed.refusal.detail}
        onRetry={
          listed.refusal.kind === "session_ended"
            ? undefined
            : () => {
                setListed({ status: "reading" });
                reread();
              }
        }
      />
    ) : null;
  const term = find.trim().toLowerCase();
  const shown = people.filter((person) => {
    if (filter !== "All" && ACCESS_NAME[person.access] !== filter) return false;
    if (term === "") return true;
    return `${nameOf(person)} ${person.email}`.toLowerCase().includes(term);
  });
  const count = (access: Access): number => people.filter((person) => person.access === access).length;
  const invitedCount = people.filter((person) => person.status === "invited").length;
  const baCount = people.filter((person) => person.kind === "ba").length;

  return (
    <DeskPage
      head={
        <>
          {head}
          {team === null ? null : inviting ? (
            <InviteForm
              api={api}
              token={token}
              clients={clients}
              onSessionEnded={onSessionEnded}
              onCancel={() => setInviting(false)}
              onInvited={(message) => {
                setInviting(false);
                say(message);
                reread();
              }}
              onPartly={() => reread()}
            />
          ) : (
            <div className="acts">
              <button type="button" className="btn btn-primary" onClick={() => setInviting(true)}>
                Invite someone
              </button>
            </div>
          )}
        </>
      }
      side={
        <section>
          <SecLabel>Find someone</SecLabel>
          <div className="panel panel-stack">
            <FieldRow label="Search">
              <input
                className="input"
                type="search"
                value={find}
                onChange={(event) => setFind(event.target.value)}
                placeholder="Name or email"
              />
            </FieldRow>
            <div className="field">
              <span className="label">Access</span>
              <Seg label="Access" value={filter} options={FILTERS} onPick={(next) => setFilter(next)} />
            </div>
            {team === null ? null : (
            <p className="help help-tight team-totals">
              {people.length} {people.length === 1 ? "person" : "people"} · {count("admin")}{" "}
              {count("admin") === 1 ? "Admin" : "Admins"} · {count("editor")}{" "}
              {count("editor") === 1 ? "Editor" : "Editors"} · {count("viewer")}{" "}
              {count("viewer") === 1 ? "Viewer" : "Viewers"}
              {invitedCount === 0 ? "" : ` · ${invitedCount} invited`}
              {baCount === 0 ? "" : ` · ${baCount} BA crew`}
              {term === "" && filter === "All" ? "" : ` · ${shown.length} shown`}
            </p>
            )}
          </div>
        </section>
      }
      foot={
        <section>
          <SecLabel
            info={
              "Everyone signs in with an emailed code and an authenticator app. Publishing, draws and wheels " +
              "ask for a fresh authenticator code each time. Export is a switch per person, and an Admin can " +
              "always export. A Viewer sees only the shows granted to them."
            }
          >
            What each access can open
          </SecLabel>
          <Matrix />
        </section>
      }
    >
      <section>
        <SecLabel>{team === null ? "People" : `People · ${shown.length}`}</SecLabel>

        {problem === null ? null : (
          <div className="team-problem">
            <AdminRefused refusal={problem.refusal} />
          </div>
        )}

        {team === null ? answer : (
        <ListState
          state="ready"
          rows={shown}
          cols={6}
          unit="people"
          empty={
            <Empty title="Nobody matches">
              <p>Try another name, address or access.</p>
            </Empty>
          }
        >
          <div className="tablewrap">
            <div className="tablescroll">
              <table className="wide team-table" aria-label="Team">
                <thead>
                  <tr>
                    <SortHead of="person" {...order}>
                      Person
                    </SortHead>
                    <SortHead of="access" {...order}>
                      Access
                    </SortHead>
                    <SortHead of="clients" {...order}>
                      Clients
                    </SortHead>
                    <SortHead of="export" {...order}>
                      Export
                    </SortHead>
                    <SortHead of="signin" {...order}>
                      Sign-in
                    </SortHead>
                    <th scope="col">
                      <span className="vh">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((person) => (
                    <Row
                      key={person.identityId}
                      person={person}
                      clients={clients}
                      busy={isBusy(person.identityId)}
                      onAccess={(to) => {
                        setAskRefusal(null);
                        setAsk({ kind: "access", person, to });
                      }}
                      onCompany={(clientId) => setAsk({ kind: "company", person, clientId })}
                      onShows={() => setAsk({ kind: "shows", person })}
                      onRemove={() => setAsk({ kind: "remove", person })}
                      onFlipClient={(clientId) => {
                        // Live clients only: an archived company the Editor holds stays
                        // held on the service whether or not it is named, and naming
                        // it in a list it cannot be newly granted from is no use.
                        const held = liveHeld(person).map((k) => k.clientId);
                        const next = held.some((id) => sameId(id, clientId))
                          ? held.filter((id) => !sameId(id, clientId))
                          : [...held, clientId];
                        const name = clients.find((k) => sameId(k.clientId, clientId))?.name ?? "that client";
                        void write(
                          person,
                          () => setEditorClients(api, token, person.identityId, next),
                          next.length > held.length
                            ? `${nameOf(person)} can now build for ${name}.`
                            : `${nameOf(person)} no longer builds for ${name}.`,
                        );
                      }}
                      onFlipExport={() =>
                        void write(
                          person,
                          () => setExport(api, token, person.identityId, !person.canExport),
                          person.canExport
                            ? `${nameOf(person)} can no longer export data.`
                            : `${nameOf(person)} can now export data for their clients.`,
                        )
                      }
                      onResend={() =>
                        void write(
                          person,
                          () => sendInvitation(api, token, person.identityId),
                          `An invite is on its way to ${person.email}.`,
                        )
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ListState>
        )}
      </section>

      {ask?.kind === "access" ? (
        <AccessDialog
          person={ask.person}
          to={ask.to}
          clients={clients}
          busy={isBusy(ask.person.identityId)}
          refusal={askRefusal}
          onCancel={() => {
            setAskRefusal(null);
            setAsk(null);
          }}
          onConfirm={(choice) => {
            const person = ask.person;
            setAskRefusal(null);
            void write(
              person,
              () =>
                changeAccess(api, token, person.identityId, {
                  access: ask.to,
                  clientId: choice.clientId,
                  clientIds: choice.clientIds,
                }),
              `${nameOf(person)} is now ${ACCESS_NAME[ask.to] === "Admin" ? "an" : "a"} ${ACCESS_NAME[ask.to]}.`,
              { inline: true },
            ).then((outcome) => {
              if (outcome.ok) setAsk(null);
              else if (outcome.refusal !== null) setAskRefusal(outcome.refusal);
            });
          }}
        />
      ) : null}

      {ask?.kind === "company" ? (
        <Confirm
          title={`Move ${nameOf(ask.person)} to ${clients.find((k) => sameId(k.clientId, ask.clientId))?.name ?? "that client"}?`}
          verb="Move"
          onCancel={() => setAsk(null)}
          onConfirm={() => {
            const person = ask.person;
            const clientId = ask.clientId;
            setAsk(null);
            void write(
              person,
              () => changeAccess(api, token, person.identityId, { access: "viewer", clientId, clientIds: null }),
              `${nameOf(person)} now belongs to ${clients.find((k) => sameId(k.clientId, clientId))?.name ?? "that client"}.`,
            );
          }}
        >
          <p>
            A Viewer belongs to one company. The shows they were granted are taken back, and they are signed out
            everywhere.
          </p>
        </Confirm>
      ) : null}

      {ask?.kind === "remove" ? (
        <Confirm
          title={
            ask.person.status === "invited" ? `Cancel the invite for ${nameOf(ask.person)}?` : `Remove ${nameOf(ask.person)}?`
          }
          verb={ask.person.status === "invited" ? "Cancel invite" : "Remove"}
          danger
          onCancel={() => setAsk(null)}
          onConfirm={() => {
            const person = ask.person;
            setAsk(null);
            void write(
              person,
              () => removeTeamMember(api, token, person.identityId),
              person.status === "invited"
                ? `Invite cancelled. ${person.email} cannot sign in.`
                : `${nameOf(person)} is signed out everywhere and can no longer sign in.`,
            );
          }}
        >
          <p>
            {ask.person.status === "invited"
              ? "They cannot sign in from now on. The invite mail carries no link that keeps working."
              : "They are signed out of every device at once. Files they exported are not recalled; the activity log keeps what they did under their name."}
          </p>
        </Confirm>
      ) : null}

      {ask?.kind === "shows" ? (
        <ShowsDialog
          api={api}
          token={token}
          // The person as the latest read has them, so a grant just made is ticked.
          person={people.find((p) => p.identityId === ask.person.identityId) ?? ask.person}
          onSessionEnded={onSessionEnded}
          landed={landed}
          onChanged={(message) => {
            say(message);
            return reread();
          }}
          onClose={() => setAsk(null)}
        />
      ) : null}
    </DeskPage>
  );
}

function Row({
  person,
  clients,
  busy,
  onAccess,
  onCompany,
  onShows,
  onRemove,
  onFlipClient,
  onFlipExport,
  onResend,
}: {
  readonly person: TeamPerson;
  readonly clients: readonly TeamClient[];
  readonly busy: boolean;
  readonly onAccess: (to: Access) => void;
  readonly onCompany: (clientId: string) => void;
  readonly onShows: () => void;
  readonly onRemove: () => void;
  readonly onFlipClient: (clientId: string) => void;
  readonly onFlipExport: () => void;
  readonly onResend: () => void;
}): ReactNode {
  const name = nameOf(person);
  return (
    <tr aria-busy={busy || undefined}>
      <td data-label="Person">
        <span className="tm-who">
          <span className="tm-av" aria-hidden="true">
            {initialsOf(person)}
          </span>
          <span>
            <b>
              {name}
              {person.you ? " · you" : ""}
            </b>
            {person.kind === "ba" ? <span className="tag tm-ba">BA</span> : null}
            <span className="cell-sub">{person.email}</span>
          </span>
        </span>
      </td>
      <td data-label="Access">
        <select
          className="input tm-sel"
          value={person.access}
          aria-label={`Access for ${name}`}
          disabled={busy || person.you}
          title={person.you ? "Ask another Admin to change your access" : undefined}
          onChange={(event) => onAccess(event.target.value as Access)}
        >
          {TEAM_ACCESS.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </td>
      <td data-label="Clients">
        {person.access === "admin" ? (
          <span className="quiet">Every client</span>
        ) : person.access === "editor" ? (
          <span className="tm-chips" role="group" aria-label={`Clients for ${name}`}>
            {clients.length === 0 ? <span className="quiet">No clients yet</span> : null}
            {clients.map((client) => {
              const held = liveHeld(person);
              const on = held.some((k) => sameId(k.clientId, client.clientId));
              // An Editor always holds at least one live client, so the last
              // one cannot be taken off here; the service refuses it as well.
              // An archived holding does not count, as it does not there.
              const last = on && held.length === 1;
              return (
                <Chip
                  key={client.clientId}
                  label={client.name}
                  on={on}
                  disabled={last}
                  {...(last ? { title: "An Editor needs at least one client" } : {})}
                  onPick={() => {
                    if (!busy && !last) onFlipClient(client.clientId);
                  }}
                />
              );
            })}
            {/* Held, archived: drawn so the row says what the Editor still
                holds, and not a control, because nothing new is built for an
                archived company and the service leaves the holding as it is. */}
            {(person.clients === "all" ? [] : person.clients)
              .filter((k) => k.archived)
              .map((k) => (
                <span key={k.clientId} className="tag tag-off tm-archived" title="Archived. Still held; nothing new is built for it.">
                  {k.name} · archived
                </span>
              ))}
          </span>
        ) : (
          <span className="tm-company">
            <select
              className="input tm-sel"
              value={person.homeClientId ?? ""}
              aria-label={`Company for ${name}`}
              disabled={busy}
              onChange={(event) => onCompany(event.target.value)}
            >
              {person.homeClientId === null ? <option value="">No company</option> : null}
              {/* An archived company is not on the live list, but it is still
                  theirs until somebody moves them, so it is drawn. */}
              {person.homeClientId !== null && !clients.some((k) => sameId(k.clientId, person.homeClientId)) ? (
                <option value={person.homeClientId} disabled>
                  {person.clients === "all" ? person.homeClientId : `${person.clients[0]?.name ?? person.homeClientId} · archived`}
                </option>
              ) : null}
              {clients.map((client) => (
                <option key={client.clientId} value={client.clientId}>
                  {client.name}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-sm btn-quiet" disabled={busy} onClick={onShows}>
              {person.shows.length === 0
                ? "Grant shows"
                : `${person.shows.length} ${person.shows.length === 1 ? "show" : "shows"}`}
            </button>
          </span>
        )}
      </td>
      <td data-label="Export">
        {person.access === "admin" ? (
          <span className="quiet">Always</span>
        ) : (
          <Switch
            on={person.canExport}
            onFlip={() => {
              if (!busy) onFlipExport();
            }}
            label={person.canExport ? "Yes" : "No"}
          />
        )}
      </td>
      <td data-label="Sign-in">
        {person.status === "invited" ? (
          <>
            <span className="tag tag-warn">Invited</span>
            <span className="cell-sub">
              {person.invitedAt === null ? "No invite sent yet" : `Invite sent ${onDay(person.invitedAt)}`}
            </span>
          </>
        ) : (
          <>
            <span>{person.secondFactor ? "Passcode and authenticator" : "Passcode only"}</span>
            <span className="cell-sub">
              {person.lastSeenAt === null ? "Not seen yet" : `Last seen ${atTime(person.lastSeenAt)}`}
            </span>
          </>
        )}
      </td>
      <td data-label="" className="tm-row-acts">
        <span className="tm-acts">
          {person.status === "invited" ? (
            <button type="button" className="btn btn-sm btn-quiet" disabled={busy} onClick={onResend}>
              {person.invitedAt === null ? "Send invite" : "Resend"}
            </button>
          ) : null}
          {person.you ? null : (
            <button type="button" className="btn btn-sm btn-quiet btn-danger" disabled={busy} onClick={onRemove}>
              {person.status === "invited" ? "Cancel invite" : "Remove"}
            </button>
          )}
        </span>
      </td>
    </tr>
  );
}

/** The access radio cards the invite form and the access dialog share. */
function AccessPick({ value, onPick }: { readonly value: Access; readonly onPick: (access: Access) => void }): ReactNode {
  return (
    <div className="tm-access" role="radiogroup" aria-label="Access">
      {TEAM_ACCESS.map(([key, label, words]) => (
        <button
          type="button"
          role="radio"
          key={key}
          aria-checked={value === key}
          className="tm-acc"
          onClick={() => onPick(key)}
        >
          <b>{label}</b>
          <span>{words}</span>
        </button>
      ))}
    </div>
  );
}

/** An Editor's clients as chips: several allowed. */
function ClientChips({
  clients,
  chosen,
  onFlip,
  label,
}: {
  readonly clients: readonly TeamClient[];
  readonly chosen: readonly string[];
  readonly onFlip: (clientId: string) => void;
  readonly label: string;
}): ReactNode {
  if (clients.length === 0) return <p className="help help-tight">There are no clients yet. Add one on Clients first.</p>;
  return (
    <span className="tm-chips" role="group" aria-label={label}>
      {clients.map((client) => (
        <Chip
          key={client.clientId}
          label={client.name}
          on={chosen.some((id) => sameId(id, client.clientId))}
          onPick={() => onFlip(client.clientId)}
        />
      ))}
    </span>
  );
}

/** A Viewer's one company: a single choice. */
function CompanyPick({
  clients,
  value,
  onPick,
  id,
}: {
  readonly clients: readonly TeamClient[];
  readonly value: string;
  readonly onPick: (clientId: string) => void;
  readonly id: string;
}): ReactNode {
  return (
    <select id={id} className="input" value={value} onChange={(event) => onPick(event.target.value)}>
      <option value="">Choose a company</option>
      {clients.map((client) => (
        <option key={client.clientId} value={client.clientId}>
          {client.name}
        </option>
      ))}
    </select>
  );
}

type Draft = {
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly access: Access;
  readonly clientIds: readonly string[];
  readonly clientId: string;
  readonly canExport: boolean;
};

const EMPTY_DRAFT: Draft = {
  email: "",
  firstName: "",
  lastName: "",
  access: "viewer",
  clientIds: [],
  clientId: "",
  canExport: false,
};

function InviteForm({
  api,
  token,
  clients,
  onSessionEnded,
  onCancel,
  onInvited,
  onPartly,
}: {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly clients: readonly TeamClient[];
  readonly onSessionEnded: (detail: string) => void;
  readonly onCancel: () => void;
  readonly onInvited: (message: string) => void;
  /** The person was added and the mail did not go: the list should show them, Invited, with nothing sent. */
  readonly onPartly: () => void;
}): ReactNode {
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  const [notSent, setNotSent] = useState<{ readonly email: string; readonly detail: string } | null>(null);

  const viewer = draft.access === "viewer";
  const editor = draft.access === "editor";
  const ready =
    /.+@.+\..+/.test(draft.email.trim()) &&
    (!viewer || (draft.clientId !== "" && draft.firstName.trim() !== "" && draft.lastName.trim() !== "")) &&
    (!editor || draft.clientIds.length > 0);

  async function send(): Promise<void> {
    setBusy(true);
    setRefusal(null);
    setNotSent(null);
    const invitee: Invitee = {
      email: draft.email.trim(),
      firstName: draft.firstName.trim() === "" ? null : draft.firstName.trim(),
      lastName: draft.lastName.trim() === "" ? null : draft.lastName.trim(),
      access: draft.access,
      clientIds: editor ? draft.clientIds : [],
      clientId: viewer ? draft.clientId : null,
      canExport: draft.access === "admin" ? true : draft.canExport,
    };
    const made = await createTeamMember(api, token, invitee);
    if (!made.ok) {
      setBusy(false);
      if (made.kind === "session_ended") {
        onSessionEnded(made.detail);
        return;
      }
      setRefusal(made);
      return;
    }
    const mailed = await sendInvitation(api, token, made.value.identityId);
    setBusy(false);
    if (!mailed.ok) {
      if (mailed.kind === "session_ended") {
        onSessionEnded(mailed.detail);
        return;
      }
      setNotSent({ email: invitee.email, detail: mailed.detail });
      setDraft(EMPTY_DRAFT);
      onPartly();
      return;
    }
    onInvited(
      `Invite sent to ${invitee.email}. They sign in with an emailed code and set up an authenticator the first time.`,
    );
  }

  return (
    <section className="panel panel-stack" aria-label="Invite someone">
      <SecLabel>Invite someone</SecLabel>
      {refusal === null ? null : <AdminRefused refusal={refusal} />}
      {notSent === null ? null : (
        <NoticeBox kind="amber" title={`${notSent.email} is on Team, and the invite did not go`}>
          <p>{notSent.detail}</p>
          <p>Press Send invite on their row to try again.</p>
        </NoticeBox>
      )}
      <FieldRow label="Email">
        <input
          className="input"
          type="email"
          value={draft.email}
          placeholder="name@company.com"
          onChange={(event) => {
            const email = event.target.value;
            setDraft((now) => ({ ...now, email }));
          }}
        />
      </FieldRow>
      <div className="pair-wide">
        <FieldRow label={viewer ? "First name" : "First name · optional"}>
          <input
            className="input"
            value={draft.firstName}
            autoComplete="off"
            onChange={(event) => {
              const firstName = event.target.value;
              setDraft((now) => ({ ...now, firstName }));
            }}
          />
        </FieldRow>
        <FieldRow label={viewer ? "Last name" : "Last name · optional"}>
          <input
            className="input"
            value={draft.lastName}
            autoComplete="off"
            onChange={(event) => {
              const lastName = event.target.value;
              setDraft((now) => ({ ...now, lastName }));
            }}
          />
        </FieldRow>
      </div>
      <div className="field">
        <span className="label">Access</span>
        <AccessPick value={draft.access} onPick={(access) => setDraft((now) => ({ ...now, access }))} />
      </div>
      {editor ? (
        <div className="field">
          <span className="label">Clients · at least one</span>
          <ClientChips
            clients={clients}
            chosen={draft.clientIds}
            label="Clients for the new Editor"
            onFlip={(clientId) =>
              setDraft((now) => ({
                ...now,
                clientIds: now.clientIds.some((id) => sameId(id, clientId))
                  ? now.clientIds.filter((id) => !sameId(id, clientId))
                  : [...now.clientIds, clientId],
              }))
            }
          />
        </div>
      ) : null}
      {viewer ? (
        <div className="field">
          <span className="label">
            <label htmlFor="team-invite-company">Company</label>
            <Info label="About a Viewer's company">
              A Viewer belongs to one company and sees only the shows you grant them. Grant shows from their row
              once they are on the list.
            </Info>
          </span>
          <CompanyPick
            id="team-invite-company"
            clients={clients}
            value={draft.clientId}
            onPick={(clientId) => setDraft((now) => ({ ...now, clientId }))}
          />
        </div>
      ) : null}
      {draft.access === "admin" ? null : (
        <Switch
          on={draft.canExport}
          onFlip={() => setDraft((now) => ({ ...now, canExport: !now.canExport }))}
          label="Can export data"
        />
      )}
      <div className="acts">
        <button type="button" className="btn btn-primary" disabled={!ready || busy} onClick={() => void send()}>
          {busy ? "Sending…" : "Send invite"}
        </button>
        <button type="button" className="btn btn-quiet" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  );
}

function AccessDialog({
  person,
  to,
  clients,
  busy,
  refusal,
  onCancel,
  onConfirm,
}: {
  readonly person: TeamPerson;
  readonly to: Access;
  readonly clients: readonly TeamClient[];
  readonly busy: boolean;
  /** The service's refusal of this change, drawn here, where the person who asked is looking. */
  readonly refusal: AdminRefusal | null;
  readonly onCancel: () => void;
  readonly onConfirm: (choice: { readonly clientId: string | null; readonly clientIds: readonly string[] | null }) => void;
}): ReactNode {
  // A live company only: an archived one is not offered, and nobody is newly
  // made a Viewer of one.
  const [clientId, setClientId] = useState(() =>
    person.homeClientId !== null && clients.some((k) => sameId(k.clientId, person.homeClientId)) ? person.homeClientId : "",
  );
  const [clientIds, setClientIds] = useState<readonly string[]>(() =>
    liveHeld(person).map((k) => k.clientId),
  );
  const name = nameOf(person);
  // A Viewer needs their company and an Editor at least one client, as the
  // invite form asks: an Editor given none can open no show (Bugbot on #206),
  // and the service refuses one too.
  const ready = (to !== "viewer" || clientId !== "") && (to !== "editor" || clientIds.length > 0);
  return (
    <Dialog
      title={`Make ${name} ${to === "admin" ? "an" : "a"} ${ACCESS_NAME[to]}?`}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn btn-quiet" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={!ready || busy}
            onClick={() =>
              onConfirm({
                clientId: to === "viewer" ? clientId : null,
                clientIds: to === "editor" ? clientIds : null,
              })
            }
          >
            {busy ? "Saving…" : `Make ${ACCESS_NAME[to]}`}
          </button>
        </>
      }
    >
      {refusal === null ? null : (
        <div className="team-dialog-problem">
          <AdminRefused refusal={refusal} />
        </div>
      )}
      <p>{TEAM_ACCESS.find(([key]) => key === to)?.[2]}</p>
      <p className="help">
        {name} is signed out everywhere, so their next sign-in opens the Console as {ACCESS_NAME[to] === "Admin" ? "an" : "a"}{" "}
        {ACCESS_NAME[to]}.
        {person.access === "viewer" ? " The shows they were granted as a Viewer are taken back." : ""}
      </p>
      {to === "editor" ? (
        <div className="field">
          <span className="label">Clients · at least one</span>
          <ClientChips
            clients={clients}
            chosen={clientIds}
            label={`Clients for ${name}`}
            onFlip={(id) =>
              setClientIds((now) => (now.some((held) => sameId(held, id)) ? now.filter((held) => !sameId(held, id)) : [...now, id]))
            }
          />
        </div>
      ) : null}
      {to === "viewer" ? (
        <div className="field">
          <label className="label" htmlFor="team-access-company">
            Company
          </label>
          <CompanyPick id="team-access-company" clients={clients} value={clientId} onPick={setClientId} />
        </div>
      ) : null}
    </Dialog>
  );
}

type Shows =
  | { readonly status: "reading" }
  | { readonly status: "read"; readonly activations: readonly ActivationLine[] }
  | { readonly status: "failed"; readonly detail: string };

/**
 * A Viewer's shows, granted and taken back one at a time (Gabe, 11 Oct).
 *
 * The company's shows come from the activation directory, the read Clients
 * uses for a company's shows, narrowed to this Viewer's company. Each switch is
 * one grant or one revocation, written as it is pressed, so there is no Save to
 * forget and nothing half-applied if the dialog is closed.
 */
function ShowsDialog({
  api,
  token,
  person,
  onSessionEnded,
  landed,
  onChanged,
  onClose,
}: {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly person: TeamPerson;
  readonly onSessionEnded: (detail: string) => void;
  /** The last read of the Team list that has landed. */
  readonly landed: number;
  /** Say it, read the list again, and answer the number of that read. */
  readonly onChanged: (message: string) => number;
  readonly onClose: () => void;
}): ReactNode {
  const [shows, setShows] = useState<Shows>({ status: "reading" });
  /**
   * What a switch was set to, held until the Team list has been read again
   * after it (`until` is that read; Infinity while the write is in flight).
   * Until then the switch draws this rather than `person.shows`, which is the
   * list as it was before the write, and refuses another press — so a second
   * press can neither grant the same show twice nor revoke the grant just
   * made (Bugbot on #206).
   */
  const [held, setHeld] = useState<
    Readonly<Record<string, { readonly on: boolean; readonly grantId: string | null; readonly until: number }>>
  >({});
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  const company = person.homeClientId;
  const name = nameOf(person);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await readActivations<ActivationDirectory>(api, token);
      if (cancelled) return;
      if (!result.ok) {
        setShows({ status: "failed", detail: result.detail });
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        return;
      }
      setShows({
        status: "read",
        activations: (result.value.activations ?? []).filter((row) => sameId(row.client.client_id, company)),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [api, token, company, onSessionEnded]);

  /** Whether this show is waiting on a write or on the read after one. */
  const pending = (activationId: string): boolean => (held[activationId]?.until ?? -1) > landed;

  /** The show as the switch should draw it: what was just written, or else what the list says. */
  const grantOf = (activationId: string): { readonly on: boolean; readonly grantId: string | null } => {
    const local = held[activationId];
    if (local !== undefined && local.until > landed) return local;
    const served = person.shows.find((show) => sameId(show.activationId, activationId)) ?? null;
    return { on: served !== null, grantId: served?.grantId ?? null };
  };

  async function flip(activation: ActivationLine): Promise<void> {
    const id = activation.activation_id;
    if (pending(id)) return;
    const now = grantOf(id);
    const title = activationName(activation.name).text;
    setHeld((was) => ({ ...was, [id]: { on: !now.on, grantId: null, until: Number.POSITIVE_INFINITY } }));
    setRefusal(null);
    const result =
      !now.on || now.grantId === null
        ? await grantShow(api, token, person.identityId, id)
        : await revokeShow(api, token, now.grantId);
    if (!result.ok) {
      setHeld((was) => {
        const next = { ...was };
        delete next[id];
        return next;
      });
      if (result.kind === "session_ended") {
        onSessionEnded(result.detail);
        return;
      }
      setRefusal(result);
      return;
    }
    const read = onChanged(!now.on ? `${name} can now see ${title}.` : `${name} can no longer see ${title}.`);
    const grantId = !now.on && "grantId" in result.value ? result.value.grantId : null;
    setHeld((was) => ({ ...was, [id]: { on: !now.on, grantId, until: read } }));
  }

  return (
    <Dialog title={`Shows for ${name}`} onClose={onClose} wide>
      <p className="help">
        A Viewer sees the dashboards, people and activity of the shows switched on here, read only. Each switch takes effect
        straight away.
      </p>
      {refusal === null ? null : <AdminRefused refusal={refusal} />}
      {company === null ? (
        <NoticeBox kind="amber">
          <p>{name} has no company. Choose one on their row first.</p>
        </NoticeBox>
      ) : shows.status === "reading" ? (
        <Loading rows={3} cols={3} label="Loading the company's shows…" />
      ) : shows.status === "failed" ? (
        <Failed what="Could not load the company's shows" detail={shows.detail} />
      ) : shows.activations.length === 0 ? (
        <Empty title="This company has no shows yet">
          <p>Shows appear here once one is opened for the company.</p>
        </Empty>
      ) : (
        <div className="tablewrap">
          <div className="tablescroll">
            <table aria-label={`Shows for ${name}`}>
              <thead>
                <tr>
                  <th scope="col">Show</th>
                  <th scope="col">State</th>
                  <th scope="col">Can see</th>
                </tr>
              </thead>
              <tbody>
                {shows.activations.map((activation) => {
                  const on = grantOf(activation.activation_id).on;
                  const state = lifecycle(activation);
                  const title = activationName(activation.name);
                  return (
                    <tr key={activation.activation_id}>
                      <td data-label="Show">
                        <b className={title.unnamed ? "quiet" : undefined}>{title.text}</b>
                        <span className="cell-sub mono">{activation.activation_id}</span>
                      </td>
                      <td data-label="State">
                        <span className={`tag ${STATE_TAG[state]}`}>{state}</span>
                      </td>
                      <td data-label="Can see">
                        <Switch
                          on={on}
                          onFlip={() => {
                            if (!pending(activation.activation_id)) void flip(activation);
                          }}
                          label={on ? "Yes" : "No"}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** The prototype's grid of what each access can open. */
function Matrix(): ReactNode {
  return (
    <div className="tablewrap">
      <div className="tablescroll">
        <table className="tm-grid" aria-label="What each access can open">
          <thead>
            <tr>
              <th scope="col">Can open</th>
              {TEAM_ACCESS.map(([key, label]) => (
                <th scope="col" key={key} className="tm-c">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TEAM_GRID.map(([what, who]) => (
              <tr key={what}>
                <td data-label="Can open">{what}</td>
                {TEAM_ACCESS.map(([key, label]) => (
                  <td key={key} data-label={label} className="tm-c">
                    {who.includes(key) ? (
                      <span className="tm-yes" aria-label={`${label} can`}>
                        ✓
                      </span>
                    ) : (
                      <span className="tm-no" aria-label={`${label} cannot`}>
                        —
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
